import { useMemo } from 'react';
import { Position, Handle } from '@xyflow/react';
import classNames from 'classnames';
import { useSelectedNodeId } from '../../contexts/useSelectedNodeId';
import { useNodesEdges } from '../../contexts/useNodesEdges';

type BaseNodeProps = {
    readonly id: string;
    readonly type: 'action' | 'decision' | 'section' | 'question';
    readonly content: string;
    readonly visConSlots: number;
    readonly options?: string[];
};

function BaseNode({ id, type, content, visConSlots, options }: BaseNodeProps) {
    const { selectedNodeId, setSelectedNodeId } = useSelectedNodeId();
    const selected = id === selectedNodeId;
    const { flowOrder, edges, incompleteNodeIds } = useNodesEdges();
    const number = flowOrder.find((f) => f.id === id)?.number ?? null;

    const connectedFlowSource = useMemo(() => {
        return edges.some((e) => e.source === id && e.sourceHandle === `${type}-flow-source`);
    }, [edges, id, type]);

    const connectedFlowTarget = useMemo(() => {
        return edges.some((e) => e.target === id && e.targetHandle === `${type}-flow-target`);
    }, [edges, id, type]);

    const isIncomplete = incompleteNodeIds.includes(id);

    const classes = classNames('node', type, {
        selected: selected,
        connected: !!number,
        incomplete: isIncomplete,
    });

    const handleDoubleClick = () => setSelectedNodeId(id);

    return (
        <div className={classes} onDoubleClick={handleDoubleClick} data-testid={`node`}>
            <Handle
                type="source"
                id={`${type}-flow-source`}
                position={Position.Right}
                isConnectable={!connectedFlowSource}
            />
            <Handle
                type="target"
                id={`${type}-flow-target`}
                position={Position.Left}
                isConnectable={!connectedFlowTarget}>
                <div className="order-tag">
                    <span>{number ?? '--'}</span>
                </div>
            </Handle>
            <div className="node__conditions handles-wrapper">
                {Array.from({ length: visConSlots }).map((_, i) => {
                    return (
                        <div key={`condition-target-${i + 1}`}>
                            <Handle
                                type="target"
                                id={`condition-target-${i + 1}`}
                                position={Position.Top}
                            />
                        </div>
                    );
                })}
                <p>Visibility</p>
            </div>
            <div className="node__content">
                <p>{content}</p>
            </div>
            <div className="node__values handles-wrapper">
                {options ? (
                    options.map((option) => {
                        return (
                            <div key={`${type}-value:${option}-source`}>
                                {option}
                                <Handle
                                    type="source"
                                    id={`${type}-value:${option}-source`}
                                    position={Position.Bottom}
                                />
                            </div>
                        );
                    })
                ) : (
                    <div>
                        Completed
                        <Handle
                            type="source"
                            id={`${type}-complete-source`}
                            position={Position.Bottom}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default BaseNode;
